import { useEffect, useState, useCallback, useRef } from 'react';
import signalRNotificationService from 'services/signalrNotificationService';
import { getUserNotificationCount } from 'services/notificationService';

/**
 * Custom hook for managing SignalR notifications
 * @param {Object} options - Configuration options
 * @param {string} options.accessToken - JWT token for authentication
 * @param {boolean} options.autoConnect - Auto-connect on mount (default: true)
 * @param {function} options.onNotification - Callback when notification received
 * @param {function} options.onError - Callback when error occurs
 * @returns {Object} - Notification state and control functions
 */
export const useSignalRNotifications = (options = {}) => {
  const {
    accessToken,
    autoConnect = true,
    onNotification,
    onError
  } = options;

  const [connectionState, setConnectionState] = useState('Disconnected');
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  // serverPushedCount: authoritative unread count pushed by the backend after each notification insert
  const [serverPushedCount, setServerPushedCount] = useState(null);
  // backendUnreadCount: last REST-fetched count -- the seed value before any SignalR push has
  // arrived, and the resync value after a real reconnect. Fetched here ONCE (centralized) rather
  // than by every consumer component independently on every mount/focus/visibility change.
  const [backendUnreadCount, setBackendUnreadCount] = useState(0);

  // Use refs to track subscriptions
  const subscriptionsRef = useRef([]);
  const isInitializedRef = useRef(false);

  const fetchBackendUnreadCount = useCallback(async () => {
    try {
      const result = await getUserNotificationCount();
      if (result.success) {
        setBackendUnreadCount(result.count);
      }
    } catch (err) {
      console.error('Failed to fetch unread notification count:', err);
    }
  }, []);

  /**
   * Connect to SignalR hub
   */
  const connect = useCallback(async () => {
    if (!accessToken) {
      console.warn('⚠️ Cannot connect: Missing access token');
      setError({ message: 'Missing access token' });
      return false;
    }

    if (isConnecting) {
      console.warn('⚠️ Connection already in progress');
      return false;
    }

    try {
      setIsConnecting(true);
      setError(null);
      setConnectionState('Connecting');
      
      await signalRNotificationService.startConnection(accessToken);
      
      setConnectionState('Connected');
      setIsConnecting(false);
      return true;
    } catch (err) {
      const errorObj = {
        message: err?.message || 'Failed to connect to notification service',
        error: err
      };
      
      setError(errorObj);
      setConnectionState('Disconnected');
      setIsConnecting(false);
      
      if (onError) {
        onError(errorObj);
      }
      
      return false;
    }
  }, [accessToken, isConnecting, onError]);

  /**
   * Disconnect from SignalR hub
   */
  const disconnect = useCallback(async () => {
    try {
      await signalRNotificationService.stopConnection();
      setConnectionState('Disconnected');
      setNotifications([]);
      setError(null);
    } catch (err) {
      console.error('Error disconnecting:', err);
    }
  }, []);

  /**
   * Clear all notifications
   */
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  /**
   * Remove a specific notification
   */
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  /**
   * Manually refresh connection (disconnect and reconnect)
   */
  const refreshConnection = useCallback(async () => {
    await disconnect();
    await connect();
  }, [connect, disconnect]);

  // Subscribe to SignalR events
  useEffect(() => {
    // Prevent multiple subscriptions
    if (subscriptionsRef.current.length > 0) {
      return;
    }

    // Notification handler
    const handleNotification = (data) => {
      const notification = {
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...data,
        receivedAt: new Date().toISOString(),
        isRead: false
      };

      setNotifications(prev => [notification, ...prev]);
      
      // Call custom callback if provided
      if (onNotification) {
        onNotification(notification);
      }
    };

    // Server-pushed count handler — authoritative DB count sent after each notification insert
    const handleNotificationCount = ({ count }) => {
      setServerPushedCount(typeof count === 'number' ? count : parseInt(count, 10) || 0);
    };

    // Connection state handlers
    const handleReconnecting = () => {
      // console.log('📡 Reconnecting to SignalR...');
      setConnectionState('Reconnecting');
    };

    const handleReconnected = ({ connectionId }) => {
      // console.log('✅ Reconnected to SignalR:', connectionId);
      setConnectionState('Connected');
      setError(null);
      // Resync the count after a real disconnect/reconnect cycle (e.g. laptop was asleep) --
      // any notifications pushed while disconnected were missed, so the count needs a REST
      // refresh here. This replaces blanket polling on every tab focus/visibility change.
      fetchBackendUnreadCount();
    };

    const handleClosed = ({ error: closeError }) => {
      // console.log('❌ SignalR connection closed');
      setConnectionState('Disconnected');
      
      if (closeError) {
        const errorObj = {
          message: 'Connection closed unexpectedly',
          error: closeError
        };
        setError(errorObj);
        
        if (onError) {
          onError(errorObj);
        }
      }
    };

    const handleError = (errorData) => {
      console.error('❌ SignalR error:', errorData);
      setError(errorData);
      
      if (onError) {
        onError(errorData);
      }
    };

    // Subscribe to all events
    const notificationSub = signalRNotificationService.subscribe('notification', handleNotification);
    const countSub = signalRNotificationService.subscribe('notificationCount', handleNotificationCount);
    const reconnectingSub = signalRNotificationService.subscribe('reconnecting', handleReconnecting);
    const reconnectedSub = signalRNotificationService.subscribe('reconnected', handleReconnected);
    const closedSub = signalRNotificationService.subscribe('closed', handleClosed);
    const errorSub = signalRNotificationService.subscribe('error', handleError);

    // Store subscription IDs for cleanup
    subscriptionsRef.current = [
      notificationSub,
      countSub,
      reconnectingSub,
      reconnectedSub,
      closedSub,
      errorSub
    ];

    // Auto-connect if enabled and not already initialized
    if (autoConnect && !isInitializedRef.current && accessToken) {
      isInitializedRef.current = true;
      connect();
      // Seed the count once via REST -- SignalR only pushes a fresh count on NEW notifications
      // (see NotificationHub.OnConnectedAsync, which doesn't send one), so without this the
      // badge would read 0 until the next notification arrives, even with existing unread ones.
      fetchBackendUnreadCount();
    }

    // Cleanup subscriptions on unmount
    return () => {
      subscriptionsRef.current.forEach(subId => {
        signalRNotificationService.unsubscribe(subId);
      });
      subscriptionsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  // Cleanup on unmount if autoConnect was used
  useEffect(() => {
    return () => {
      if (autoConnect && isInitializedRef.current) {
        disconnect();
        isInitializedRef.current = false;
      }
    };
  }, [autoConnect, disconnect]);

  // Mark notification as read
  const markAsRead = useCallback((id) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, isRead: true } : n)
    );
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, isRead: true }))
    );
    setServerPushedCount(0);
    setBackendUnreadCount(0);
  }, []);

  // Get unread count — prefer the live server-pushed count (authoritative DB count sent after
  // each new notification); fall back to the last REST-fetched count before any push has arrived.
  const unreadCount = serverPushedCount !== null ? serverPushedCount : backendUnreadCount;

  // Reset server-pushed count (call when panel opens and REST count takes over)
  const resetServerPushedCount = () => setServerPushedCount(null);

  return {
    // State
    connectionState,
    notifications,
    unreadCount,
    error,
    isConnecting,
    isConnected: connectionState === 'Connected',

    // Actions
    connect,
    disconnect,
    // Manual resync with the backend -- call after a mutation (mark as read / mark all as read)
    // or a deliberate user action (opening the notifications panel), not on passive
    // reload/focus/visibility, which is what this replaces.
    refreshUnreadCount: fetchBackendUnreadCount,
    refreshConnection,
    clearNotifications,
    removeNotification,
    markAsRead,
    markAllAsRead,
    resetServerPushedCount,
    
    // Utility
    connectionId: signalRNotificationService.getConnectionId(),
  };
};

export default useSignalRNotifications;

